import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { ExternalLink, Eye, EyeOff, Pencil } from 'lucide-react';

import type { GetUserQuery } from '@graphql/__generated__/graphql';
import { ProfileCompletionScore } from '@modules/_shared/components/profile-completion/ProfileCompletionScore';
import { getAgentDrawerConfig } from '@modules/_shared/components/visibility-drawer/configs/agentDrawerConfig';
import { VisibilityDrawer } from '@modules/_shared/components/visibility-drawer/VisibilityDrawer';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAgentLiveProfileUrl } from '@modules/_shared/utils/agentUrls';
import { getTwoYearsAgo } from '@modules/_shared/utils/dates';
import { calculateProfileCompletionScore } from '@modules/_shared/utils/profileCompletionScore';
import { GET_USER_PROFILE_COMPLETION } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

type AgentProfileCompletionProps = {
  user: NonNullable<GetUserQuery['users_by_pk']>;
};

export const AgentProfileCompletion = ({
  user,
}: AgentProfileCompletionProps) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const isSwiss = me?.tenant?.country_code === 'CH';
  const twoYearsAgo = useMemo(() => getTwoYearsAgo(), []);
  const [editOpen, setEditOpen] = useState(false);

  const liveProfileUrl =
    user.show_in_agency_pages && me?.tenant_id
      ? getAgentLiveProfileUrl(me.tenant_id, locale, user.agent_slug, user.id)
      : null;

  const { data } = useQuery(GET_USER_PROFILE_COMPLETION, {
    variables: { userId: user.id, since: twoYearsAgo.toISOString() },
  });

  const teamId = user.default_team_id ?? user.teams_users[0]?.team.id ?? null;
  const canEdit = me?.is_admin || me?.id === user.id;

  const completionResult = useMemo(
    () =>
      data?.users_by_pk
        ? calculateProfileCompletionScore(data.users_by_pk, t, isSwiss, 'agent')
        : null,
    [data?.users_by_pk, t, isSwiss],
  );

  if (!completionResult) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        mt: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ProfileCompletionScore
          completionResult={completionResult}
          size="small"
          trailing={
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.25 }}>
              <Tooltip
                title={
                  user.show_in_agency_pages
                    ? t('Profile: Published')
                    : t('Profile: Unpublished')
                }
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: user.show_in_agency_pages
                      ? 'success.main'
                      : customPalette.gray400,
                  }}
                >
                  {user.show_in_agency_pages ? (
                    <Eye size={16} />
                  ) : (
                    <EyeOff size={16} />
                  )}
                </Box>
              </Tooltip>
              {liveProfileUrl && (
                <Tooltip title={t('Open live profile')}>
                  <IconButton
                    size="small"
                    component="a"
                    href={liveProfileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 24,
                      height: 24,
                      padding: 0.25,
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ExternalLink size={14} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          }
        />
      </Box>
      {canEdit && teamId && (
        <>
          <Tooltip title={t('Edit profile')}>
            <IconButton
              size="small"
              onClick={() => setEditOpen(true)}
              sx={{
                width: 28,
                height: 28,
                color: 'text.secondary',
                backgroundColor: 'action.hover',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: 'action.selected',
                  color: 'primary.main',
                },
              }}
            >
              <Pencil size={14} />
            </IconButton>
          </Tooltip>
          {editOpen && (
            <VisibilityDrawer
              onClose={() => setEditOpen(false)}
              entityId={user.id}
              teamId={teamId}
              config={getAgentDrawerConfig(t)}
              title={t('Edit profile')}
            />
          )}
        </>
      )}
    </Box>
  );
};
